/**
 * Email Tracking Utilities
 * 
 * Tracks which template, subject line, and instruction were used for each sent email.
 * This enables reply tracking to measure template performance.
 */

import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { ref, update, get } from 'firebase/database';
import { firestore, database } from '../../firebaseConfig';
import { logSalesAnalytics, logEmailScore } from '../analyticsTracker';
import { minimalSignalsSnapshot } from '../leadSignalEngine';
import { ACTIVE_EMAIL_ACTION_TYPES } from '../../shared/flowTouchpoints';

/**
 * Extracts the ID from a subject line or instruction object
 * Handles both new format {id, text} and old format (plain string)
 */
export const extractItemId = (item) => {
  if (!item) return null;
  if (typeof item === 'object' && item.id) {
    return item.id;
  }
  // For legacy string format, generate a hash-based ID
  if (typeof item === 'string') {
    return `legacy_${hashString(item)}`;
  }
  return null;
};

/**
 * Extracts the text from a subject line or instruction object
 * Handles both new format {id, text} and old format (plain string)
 */
export const extractItemText = (item) => {
  if (!item) return '';
  if (typeof item === 'object' && item.text !== undefined) {
    return item.text;
  }
  if (typeof item === 'string') {
    return item;
  }
  return '';
};

/**
 * Simple hash function for legacy string IDs
 */
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Maps topic names to their phase identifiers (used for searching)
 */
const topicTypeMapping = {
  "Introduce Yourself": "intro",
  "Follow Up": "follow_up_1",
  "Second Follow Up": "follow_up_2",
  "Third Follow Up": "follow_up_3"
};

/**
 * Maps contact status to the corresponding stage key used in lead_templates.
 */
const STATUS_TO_STAGE = {
  'Not Contacted': 'intro',
  'Introduced': 'follow_up_1',
  'First Follow-up': 'follow_up_2',
  'Second Follow-up': 'follow_up_3',
};

/**
 * Resolves the correct template ID for a contact based on its current stage.
 * Priority: lead_templates[currentStage] > lead_template (intro stage only)
 *
 * The single `lead_template` field is treated as the intro-stage default. It
 * MUST NOT leak into follow-up stages — otherwise an intro template stays
 * "sticky" on the contact and overrides the globally selected follow-up
 * template chosen in Settings. For follow-up stages without a per-stage
 * override we return null so the caller falls back to the user's global
 * selectedTopics (status-based selection in NormalModeEmail).
 *
 * @param {object} contact - The contact/row data
 * @returns {string|null} The resolved template ID or null
 */
export const resolveTemplateForContact = (contact) => {
  if (!contact) return null;

  const stageMap = contact.lead_templates;
  const status = contact.lead_contacted || 'Not Contacted';
  const currentStage = STATUS_TO_STAGE[status]; // undefined for unknown statuses

  // Per-stage override always wins when explicitly set for this stage.
  if (currentStage && stageMap && typeof stageMap === 'object' && stageMap[currentStage]) {
    return stageMap[currentStage];
  }

  // Single default `lead_template` only applies to the intro stage
  // (Not Contacted / unknown status). Follow-ups must use the global
  // selection from Settings unless an explicit per-stage override exists.
  if (!currentStage || currentStage === 'intro') {
    return contact.lead_template || null;
  }

  return null;
};

/**
 * Determines whether a contact is currently sitting on a flow's "Send Email"
 * step, and returns the template/sender configuration attached to that step.
 *
 * This is the highest-priority source for template + sender selection in the
 * Targets list (Normal / Bulk mode): when a flow is active and its current step
 * is an email step, the flow config wins over the per-contact override, AI tag
 * and status-based defaults. A missing templateId falls back to the existing
 * default logic (the caller simply gets templateId === '').
 *
 * @param {object} contact - The contact/row data
 * @param {Array}  warmupFlows - Loaded warmup flow definitions
 * @param {Array}  leadFlows - Loaded retargeting flow definitions
 * @returns {object|null} { isFlowEmailStep, flowId, flowField, stepIndex,
 *                          templateId, senderMode, senderId, senderPool, flowSenderId }
 */
export const resolveFlowEmailStep = (contact, warmupFlows = [], leadFlows = []) => {
  if (!contact) return null;

  const candidates = [
    { state: contact.warmupFlow, flows: warmupFlows, flowField: 'warmupFlow' },
    { state: contact.activeFlow, flows: leadFlows, flowField: 'activeFlow' },
  ];

  for (const { state, flows, flowField } of candidates) {
    if (!state || state.currentStepIndex < 0 || !state.flowId) continue;
    if (!Array.isArray(flows) || flows.length === 0) continue;

    const flow = flows.find((f) => f.id === state.flowId);
    if (!flow?.steps) continue;

    const step = flow.steps[state.currentStepIndex];
    if (!step) continue;

    const isEmailStep = step.nodeType === 'action' && ACTIVE_EMAIL_ACTION_TYPES.includes(step.type);
    if (!isEmailStep) continue;

    return {
      isFlowEmailStep: true,
      flowId: flow.id,
      flowField,
      stepIndex: state.currentStepIndex,
      templateId: step.templateId || '',
      senderMode: step.senderMode || 'default',
      senderId: step.senderId || '',
      senderPool: Array.isArray(step.senderPool) ? step.senderPool : [],
      flowSenderId: state.flowSenderId || null,
    };
  }

  return null;
};

/**
 * Finds a topic by its name or type in userTopics
 * 🔥 FIXED: Also searches by mapped type (e.g., "Introduce Yourself" → "intro")
 */
export const findTopicInUserTopics = (userTopics, topicNameOrType) => {
  if (!userTopics || !topicNameOrType) return null;
  
  const mappedType = topicTypeMapping[topicNameOrType];
  console.log('📧 [TRACKING-UTILS] Searching for topic:', topicNameOrType, '→ mappedType:', mappedType);
  
  let matchingTopic = null;
  let matchingTopicId = null;
  
  const searchScopes = [
    ['userTopics', Object.entries(userTopics).filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v) && v.topic)],
    ['selectedTopics', userTopics.selectedTopics ? Object.entries(userTopics.selectedTopics) : []],
    ['customTopics', userTopics.customTopics ? Object.entries(userTopics.customTopics) : []],
  ];

  // Pass 1: exact name match (topicData.topic === topicNameOrType)
  for (const [scopeName, entries] of searchScopes) {
    for (const [topicId, topicData] of entries) {
      if (topicData.topic === topicNameOrType) {
        matchingTopic = topicData;
        matchingTopicId = topicId;
        console.log(`📧 [TRACKING-UTILS] Found by exact name in ${scopeName}:`, topicId, topicData.topic);
        break;
      }
    }
    if (matchingTopic) break;
  }

  // Pass 2: type-based fallback
  if (!matchingTopic) {
    for (const [scopeName, entries] of searchScopes) {
      for (const [topicId, topicData] of entries) {
        if (topicData.type === topicNameOrType || (mappedType && topicData.type === mappedType)) {
          matchingTopic = topicData;
          matchingTopicId = topicId;
          console.log(`📧 [TRACKING-UTILS] Found by type in ${scopeName}:`, topicId, topicData.topic);
          break;
        }
      }
      if (matchingTopic) break;
    }
  }
  
  if (!matchingTopic) {
    console.log('📧 [TRACKING-UTILS] No topic found for:', topicNameOrType);
  }
  
  return matchingTopic ? { ...matchingTopic, id: matchingTopicId } : null;
};

/**
 * Records email send data for a contact
 * Stores in Firestore under the contact document
 * 
 * @param {string} userId - The user's ID
 * @param {string} contactId - The contact's document ID
 * @param {Object} sendData - Data about the sent email
 * @param {string} sendData.templateId - ID of the template used
 * @param {string} sendData.templateName - Name of the template
 * @param {string} sendData.phase - Email phase (intro, follow_up_1, etc.)
 * @param {string} sendData.subjectLineId - ID of the subject line used
 * @param {string} sendData.subjectLineText - The actual subject line text
 * @param {string} sendData.instructionId - ID of the instruction used
 * @param {string} sendData.senderEmail - Email address used to send
 */
export const recordEmailSend = async (userId, contactId, sendData) => {
  if (!userId || !contactId) {
    console.warn('📧 [EMAIL-TRACKING] Missing userId or contactId');
    return false;
  }

  try {
    const emailRecord = {
      sentAt: new Date().toISOString(),
      timestamp: Date.now(),
      templateId: sendData.templateId || null,
      templateName: sendData.templateName || null,
      phase: sendData.phase || null,
      subjectLineId: sendData.subjectLineId || null,
      subjectLineText: sendData.subjectLineText || null,
      instructionId: sendData.instructionId || null,
      senderEmail: sendData.senderEmail || null,
      ...(sendData.leadSignals ? { leadSignalsSnapshot: minimalSignalsSnapshot(sendData.leadSignals) } : {}),
    };

    console.log('📧 [EMAIL-TRACKING] Recording email send:', emailRecord);

    // Update contact document in Firestore
    const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);
    await updateDoc(contactDocRef, {
      emailHistory: arrayUnion(emailRecord),
      lastEmailSent: emailRecord
    });

    console.log('📧 [EMAIL-TRACKING] ✅ Email send recorded for contact:', contactId);
    return true;

  } catch (error) {
    console.error('📧 [EMAIL-TRACKING] ❌ Error recording email send:', error);
    return false;
  }
};

/**
 * Updates template statistics after sending an email
 * Increments counters for the specific subject line and instruction used.
 * For Voice-Clone-mode templates, also increments the per-variant counter
 * so we can A/B test structures and pick winners by reply rate.
 *
 * @param {string} userId         - The user's ID
 * @param {string} templateId     - The template ID (e.g., "custom_1701619200000")
 * @param {string} subjectLineId  - ID of the subject line used (standard mode)
 * @param {string} instructionId  - ID of the instruction used (standard mode)
 * @param {string} [variantId]    - ID of the structure variant used (voice-clone mode, optional)
 */
export const updateTemplateStats = async (userId, templateId, subjectLineId, instructionId, variantId) => {
  if (!userId || !templateId) {
    console.warn('📧 [EMAIL-TRACKING] Missing userId or templateId for stats update');
    return false;
  }

  try {
    // Determine the path based on templateId format
    let templatePath;
    if (templateId.startsWith('custom_')) {
      templatePath = `/World/${userId}/settings/customTopics/${templateId}`;
    } else {
      templatePath = `/World/${userId}/settings/selectedTopics/${templateId}`;
    }

    const templateRef = ref(database, templatePath);
    const snapshot = await get(templateRef);
    
    if (!snapshot.exists()) {
      console.warn('📧 [EMAIL-TRACKING] Template not found:', templateId);
      return false;
    }

    const currentData = snapshot.val();
    const stats = currentData.stats || {
      totalSent: 0,
      subjectLineStats: {},
      instructionStats: {},
      variantStats: {},
    };

    // Increment total sent
    stats.totalSent = (stats.totalSent || 0) + 1;

    // Increment subject line stats
    if (subjectLineId) {
      if (!stats.subjectLineStats[subjectLineId]) {
        stats.subjectLineStats[subjectLineId] = { sent: 0, replies: 0 };
      }
      stats.subjectLineStats[subjectLineId].sent = (stats.subjectLineStats[subjectLineId].sent || 0) + 1;
    }

    // Increment instruction stats
    if (instructionId) {
      if (!stats.instructionStats[instructionId]) {
        stats.instructionStats[instructionId] = { sent: 0, replies: 0 };
      }
      stats.instructionStats[instructionId].sent = (stats.instructionStats[instructionId].sent || 0) + 1;
    }

    // Voice Clone Mode — per-structure-variant tracking. Same shape as the
    // other counters so the existing reply-tracking pipeline can credit them
    // symmetrically.
    if (variantId) {
      if (!stats.variantStats) stats.variantStats = {};
      if (!stats.variantStats[variantId]) {
        stats.variantStats[variantId] = { sent: 0, replies: 0 };
      }
      stats.variantStats[variantId].sent = (stats.variantStats[variantId].sent || 0) + 1;
    }

    // Update the template with new stats
    await update(templateRef, { stats });

    console.log('📧 [EMAIL-TRACKING] ✅ Template stats updated:', {
      templateId,
      totalSent: stats.totalSent,
      subjectLineId,
      instructionId,
      variantId,
    });

    return true;

  } catch (error) {
    console.error('📧 [EMAIL-TRACKING] ❌ Error updating template stats:', error);
    return false;
  }
};

/**
 * Helper function to select a random item from an array and return both the item and its ID
 * This replaces the simple getRandomFromArray to enable tracking
 * 
 * @param {Array} array - Array of items (can be strings or {id, text} objects)
 * @returns {Object} - { item, id, text } or null if array is empty
 */
export const selectRandomWithTracking = (array) => {
  if (!Array.isArray(array) || array.length === 0) {
    return null;
  }

  const randomIndex = Math.floor(Math.random() * array.length);
  const item = array[randomIndex];

  return {
    item,
    id: extractItemId(item),
    text: extractItemText(item),
    index: randomIndex
  };
};

/**
 * Maps topic names to their phase identifiers
 * Uses the shared topicTypeMapping defined above
 */
export const getPhaseFromTopic = (topicName) => {
  return topicTypeMapping[topicName] || 'custom';
};

/**
 * Complete tracking function that records both contact history and template stats
 * Call this after successfully sending an email
 */
export const trackEmailSend = async ({
  userId,
  contactId,
  templateId,
  templateName,
  topicName,
  subjectLineId,
  subjectLineText,
  instructionId,
  senderEmail,
  userTopics,
  leadSignals
}) => {
  console.log('📧 [EMAIL-TRACKING] Starting complete email tracking...');

  // Find template ID if not provided
  let finalTemplateId = templateId;
  let finalTemplateName = templateName;
  
  if (!finalTemplateId && topicName && userTopics) {
    const foundTopic = findTopicInUserTopics(userTopics, topicName);
    if (foundTopic) {
      finalTemplateId = foundTopic.id;
      finalTemplateName = foundTopic.topic || topicName;
    }
  }

  const phase = getPhaseFromTopic(topicName);

  // Record to contact
  const contactRecorded = await recordEmailSend(userId, contactId, {
    templateId: finalTemplateId,
    templateName: finalTemplateName,
    phase,
    subjectLineId,
    subjectLineText,
    instructionId,
    senderEmail,
    leadSignals: leadSignals || null,
  });

  // Update template stats
  const statsUpdated = await updateTemplateStats(
    userId,
    finalTemplateId,
    subjectLineId,
    instructionId
  );

  console.log('📧 [EMAIL-TRACKING] Complete tracking result:', {
    contactRecorded,
    statsUpdated,
    templateId: finalTemplateId,
    phase
  });

  // Fire-and-forget: anonymized sales analytics
  try {
    logSalesAnalytics(userId, contactId, {
      touchpointType: phase === 'intro' ? 'cold_email' : 'follow_up_email',
      stage: phase,
    });
  } catch (_) { /* never break existing flow */ }

  return { contactRecorded, statsUpdated };
};

/**
 * Returns the pacing used for picking the right TEMPLATE VARIANT.
 *
 * SCOPE — read carefully:
 *   • This value ONLY governs which `topicSpecificNote` variant is selected
 *     when a template ships multiple variants tagged with `pacing`.
 *   • It does NOT influence the email's TONE downstream. The selected
 *     template/variant alone dictates the tone (via its greeting prefix
 *     configuration).
 *   • If a contact is "formal" but only casual variants exist, the casual
 *     variant is used and the email STAYS casual. The contact's pacing
 *     does not "rewrite" the chosen template.
 *
 * We deliberately do NOT auto-formalize for DM recipients. Cold outreach
 * in DACH/B2B is overwhelmingly "du"-form today, and the legacy DM-auto-
 * formal heuristic forced casual templates into formal mode against the
 * user's intent.
 *
 * @param {string|null} contactPacing - The contact's lead_pacing value
 * @param {boolean} _isDecisionMakerRecipient - (kept for backwards compat, unused)
 * @param {string|null} _templateEmailPref - (kept for backwards compat, unused)
 * @returns {string|null}
 */
// eslint-disable-next-line no-unused-vars
export const getEffectivePacing = (contactPacing, _isDecisionMakerRecipient, _templateEmailPref = null) => {
  if (contactPacing) return contactPacing;
  return null;
};

/**
 * Pacing-aware variant selection from topicSpecificNotes array.
 * Picks a variant matching the contact's lead_pacing with cascading fallback:
 *   1. Exact pacing match
 *   2. Nearest pacing neighbor (formal↔neutral↔casual)
 *   3. Variants with no pacing assigned
 *   4. Any remaining variant (pure random)
 *
 * @param {Array} instructions - Array of {id, text, pacing?} objects
 * @param {string|null} contactPacing - The contact's lead_pacing value
 * @returns {Object|null} - Selected instruction object, or null
 */
export const selectInstructionByPacing = (instructions, contactPacing, excludeId = null) => {
  if (!Array.isArray(instructions) || instructions.length === 0) return null;

  const FALLBACK_ORDER = {
    formal:  ['formal', 'neutral', 'casual'],
    neutral: ['neutral', 'formal', 'casual'],
    casual:  ['casual', 'neutral', 'formal'],
  };

  // excludeId may be a single id OR an array of ids (used by the rotation /
  // shuffle-bag selection below, which excludes every variant already used in
  // the current cycle). We fall back to the full pool if the exclusion would
  // empty the candidate list.
  const excludeSet = Array.isArray(excludeId)
    ? new Set(excludeId.filter(Boolean))
    : (excludeId ? new Set([excludeId]) : new Set());

  const withoutLast = (arr) => {
    if (excludeSet.size === 0 || arr.length <= 1) return arr;
    const filtered = arr.filter(i => !excludeSet.has(extractItemId(i)));
    return filtered.length > 0 ? filtered : arr;
  };

  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

  if (contactPacing && FALLBACK_ORDER[contactPacing]) {
    for (const pacing of FALLBACK_ORDER[contactPacing]) {
      const matches = instructions.filter(i => i.pacing === pacing);
      if (matches.length > 0) return pick(withoutLast(matches));
    }
    const unassigned = instructions.filter(i => !i.pacing);
    if (unassigned.length > 0) return pick(withoutLast(unassigned));
  }

  if (contactPacing === 'neutral') {
    const neutralMatches = instructions.filter(i => i.pacing === 'neutral');
    if (neutralMatches.length > 0) return pick(withoutLast(neutralMatches));
  }

  return pick(withoutLast(instructions));
};

/* ────────────────────────────────────────────────────────────────────────────
 * VARIANT ROTATION (Shuffle-Bag)
 *
 * Problem: pure random selection of template variants clusters on a few
 * variants — with 5 variants you often see the same 2–3 over and over and
 * rarely a full sweep of all 5.
 *
 * Solution: a "shuffle bag" / round-robin. For each template we remember which
 * variant IDs have already been used in the CURRENT cycle and only pick from
 * the ones that haven't been used yet. Once every variant has been used the
 * cycle resets, so with 5 variants we go through all 5 before any repeats.
 *
 * State is persisted in localStorage (keyed per template) so the rotation stays
 * consistent across Normal-/Bulk-/Auto-Send-mode AND across page reloads, and
 * mirrored in memory so a fast bulk loop stays consistent within a single run.
 * ──────────────────────────────────────────────────────────────────────────── */

const ROTATION_STORAGE_KEY = 'bc_variantRotation_v1';
let _rotationCache = null;

const _loadRotationState = () => {
  if (_rotationCache) return _rotationCache;
  try {
    const raw = (typeof localStorage !== 'undefined')
      ? localStorage.getItem(ROTATION_STORAGE_KEY)
      : null;
    _rotationCache = raw ? JSON.parse(raw) : {};
  } catch (_) {
    _rotationCache = {};
  }
  return _rotationCache;
};

const _saveRotationState = (state) => {
  _rotationCache = state;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ROTATION_STORAGE_KEY, JSON.stringify(state));
    }
  } catch (_) { /* private mode / quota — in-memory cache still works */ }
};

/**
 * Returns the variant IDs already consumed in the current rotation cycle for
 * `rotationKey`. IDs no longer present in `availableIds` (e.g. a variant was
 * deleted) are pruned so the cycle self-heals when variants change.
 */
const getUsedInRotationCycle = (rotationKey, availableIds) => {
  if (!rotationKey) return [];
  const state = _loadRotationState();
  const used = Array.isArray(state[rotationKey]) ? state[rotationKey] : [];
  return used.filter(id => availableIds.includes(id));
};

/**
 * Records that `chosenId` was used for `rotationKey`. When the whole set has
 * been used the cycle resets — keeping only the just-picked ID so it isn't
 * immediately repeated as the first pick of the next cycle.
 */
export const recordVariantRotation = (rotationKey, chosenId, availableIds) => {
  if (!rotationKey || !chosenId || !Array.isArray(availableIds) || availableIds.length === 0) return;
  const state = _loadRotationState();
  let used = (Array.isArray(state[rotationKey]) ? state[rotationKey] : [])
    .filter(id => availableIds.includes(id));
  if (!used.includes(chosenId)) used.push(chosenId);
  if (used.length >= availableIds.length) {
    // Cycle complete → start fresh; remember last pick to avoid back-to-back repeat.
    used = availableIds.length > 1 ? [chosenId] : [];
  }
  state[rotationKey] = used;
  _saveRotationState(state);
};

/**
 * Rotation-aware variant selection. Picks a variant that hasn't been used yet
 * in the current cycle for `rotationKey`, records it, and resets the cycle once
 * all variants have been used. Falls back to plain pacing-based random when no
 * rotationKey is given or there is only a single distinct variant.
 *
 * @param {Array} instructions   - Array of {id, text, pacing?} objects (or strings)
 * @param {string|null} contactPacing - Contact's lead_pacing (currently unused → null)
 * @param {string|null} rotationKey   - Stable key per template (e.g. templateId)
 * @param {string|null} extraExcludeId - Additional id to avoid (e.g. on "regenerate")
 * @returns {Object|null} - Selected instruction, or null
 */
export const selectInstructionWithRotation = (
  instructions,
  contactPacing = null,
  rotationKey = null,
  extraExcludeId = null
) => {
  if (!Array.isArray(instructions) || instructions.length === 0) return null;

  const availableIds = instructions.map(extractItemId).filter(Boolean);
  const distinctIds = [...new Set(availableIds)];

  // Rotation is meaningless with 0/1 variants or without a stable key.
  if (distinctIds.length <= 1 || !rotationKey) {
    return selectInstructionByPacing(instructions, contactPacing, extraExcludeId);
  }

  // Namespace the key so a template's VARIANT rotation never collides with its
  // SUBJECT-LINE rotation (both are keyed per templateId).
  const nsKey = `var:${rotationKey}`;
  const exclude = getUsedInRotationCycle(nsKey, distinctIds);
  if (extraExcludeId && !exclude.includes(extraExcludeId)) exclude.push(extraExcludeId);

  const selected = selectInstructionByPacing(instructions, contactPacing, exclude);
  const chosenId = extractItemId(selected);
  if (chosenId) recordVariantRotation(nsKey, chosenId, distinctIds);
  return selected;
};

/**
 * Rotation-aware random selection for subject lines (or any {id,text} / string
 * array). Same shuffle-bag behaviour as selectInstructionWithRotation, but
 * returns the tracking shape of selectRandomWithTracking ({item, id, text})
 * so existing subject-line call sites stay drop-in compatible.
 *
 * @param {Array} array        - Subject line array ({id, text} objects or strings)
 * @param {string|null} rotationKey - Stable key per template (e.g. templateId)
 * @returns {Object|null} - { item, id, text, index } or null
 */
export const selectRandomWithRotation = (array, rotationKey = null) => {
  if (!Array.isArray(array) || array.length === 0) return null;

  const ids = array.map(extractItemId).filter(Boolean);
  const distinctIds = [...new Set(ids)];

  // Rotation is meaningless with 0/1 distinct items or without a stable key.
  if (distinctIds.length <= 1 || !rotationKey) {
    return selectRandomWithTracking(array);
  }

  const nsKey = `subj:${rotationKey}`;
  const used = getUsedInRotationCycle(nsKey, distinctIds);
  const usedSet = new Set(used);

  const candidatePool = array.filter(item => !usedSet.has(extractItemId(item)));
  const pool = candidatePool.length > 0 ? candidatePool : array;

  const item = pool[Math.floor(Math.random() * pool.length)];
  const chosenId = extractItemId(item);
  if (chosenId) recordVariantRotation(nsKey, chosenId, distinctIds);

  return {
    item,
    id: chosenId,
    text: extractItemText(item),
    index: array.indexOf(item),
  };
};

/**
 * Maps a selected template variant's pacing classification to the greeting
 * tone signal sent to the backend (WorldEmailCreator).
 *
 *   • 'formal' → formal greeting
 *   • 'casual' → casual greeting
 *   • 'neutral' / unclassified → 'auto' (the AI decides the tone, using the
 *     combined configured greeting prefixes)
 *
 * This is the ONLY thing that drives the greeting tone in Standard Template
 * Mode — the contact's lead_pacing is no longer used here.
 *
 * @param {string|null|undefined} variantPacing
 * @returns {'formal'|'casual'|'auto'}
 */
export const greetingToneFromVariant = (variantPacing) =>
  (variantPacing === 'formal' || variantPacing === 'casual') ? variantPacing : 'auto';

/**
 * Couples a pre-send score to the lead's analytics profile (fire-and-forget).
 * Call this after a successful send when response contains preSendScore data.
 */
export const coupleScoreToAnalytics = (userId, contactId, preSendScore) => {
  try {
    if (!preSendScore || !userId || !contactId) return;
    logEmailScore(userId, contactId, {
      compositeScore: preSendScore.compositeScore,
      buyerValue: preSendScore.buyerValue,
      deliverabilityRisk: preSendScore.deliverabilityRisk,
      recommendation: preSendScore.recommendation,
      contentHash: preSendScore.contentHash,
    });
  } catch (_) { /* never break existing flow */ }
};

const INSTRUCTION_BLOCK_REGEX = /(?<!\{)\{(?!\{)((?:[^{}]|\{\{[^{}]*\}\})+)\}(?!\})/g;

/**
 * Returns true when the text contains at least one {instruction} block
 * (ignores {{variable}} double-brace placeholders).
 */
export const hasInstructionBlocks = (text) => {
  if (!text) return false;
  INSTRUCTION_BLOCK_REGEX.lastIndex = 0;
  return INSTRUCTION_BLOCK_REGEX.test(text);
};

/**
 * Removes all {instruction} blocks from the text, keeping static parts
 * and {{variable}} placeholders intact. Used as fallback when AI resolution fails.
 */
export const stripInstructionBlocks = (text) => {
  if (!text) return '';
  return text
    .replace(INSTRUCTION_BLOCK_REGEX, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s*[-–—,]\s*/, '')
    .replace(/\s*[-–—,]\s*$/, '')
    .trim();
};

export default {
  extractItemId,
  extractItemText,
  resolveFlowEmailStep,
  findTopicInUserTopics,
  recordEmailSend,
  updateTemplateStats,
  selectRandomWithTracking,
  selectRandomWithRotation,
  selectInstructionByPacing,
  selectInstructionWithRotation,
  recordVariantRotation,
  getEffectivePacing,
  getPhaseFromTopic,
  trackEmailSend,
  coupleScoreToAnalytics,
  hasInstructionBlocks,
  stripInstructionBlocks,
};

